import { type ApiRequestOptions, apiJson } from "@/api/apiClient";

import type { KnowledgeGraph, KnowledgeGraphList, KnowledgeGraphVisualization } from "@/types/knowledgeGraph";

// --- Node / Edge types ---

export interface GraphNode {
	id: string;
	graphId: string;
	name: string;
	elementType: string;
	typeId: string | null;
	metadata: Record<string, unknown>;
	createdAt: string;
	updatedAt: string;
}

export interface GraphEdge {
	id: string;
	graphId: string;
	predicateId: string | null;
	source: string;
	target: string;
	tuplePosition: number | null;
	label: string;
	createdAt: string;
	updatedAt: string;
}

/**
 * Get all knowledge graphs
 */
export const getKnowledgeGraphs = async (options?: ApiRequestOptions): Promise<KnowledgeGraphList> => {
	return apiJson<KnowledgeGraphList>("/knowledge-graph", {
		signal: options?.signal,
	});
};

/**
 * Get a knowledge graph by ID
 */
export const getKnowledgeGraph = async (id: string, options?: ApiRequestOptions): Promise<KnowledgeGraph> => {
	return apiJson<KnowledgeGraph>(`/knowledge-graph/${id}`, {
		signal: options?.signal,
	});
};

/**
 * Get knowledge graph visualization (nodes and edges)
 */
export const getKnowledgeGraphVisualization = async (
	id: string,
	options?: ApiRequestOptions,
): Promise<KnowledgeGraphVisualization> => {
	return apiJson<KnowledgeGraphVisualization>(`/knowledge-graph/${id}/visualization`, {
		signal: options?.signal,
	});
};

/** Tree node for class hierarchy (rdfs:subClassOf) */
export interface HierarchyNode {
	id: string;
	label: string;
	children?: HierarchyNode[];
	instanceCount?: number;
}

export interface KnowledgeGraphHierarchyResponse {
	roots: HierarchyNode[];
}

/**
 * Get class hierarchy for a knowledge graph (RDF/GraphDB ontology)
 */
export const getKnowledgeGraphHierarchy = async (
	id: string,
	options?: ApiRequestOptions,
): Promise<KnowledgeGraphHierarchyResponse> => {
	return apiJson<KnowledgeGraphHierarchyResponse>(`/knowledge-graph/${id}/hierarchy`, {
		signal: options?.signal,
	});
};

// ── Workspace ─────────────────────────────────────────────────────────────────

export interface WorkspaceItem {
	id: string;
	name: string;
	ownerId: string;
	graphdbSyncedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface WorkspaceKGNode {
	id: string;
	name: string;
	description: string | null;
	classCount: number;
	classLabels: string[];
}

export interface WorkspaceKGEdge {
	id: string;
	source: string;
	target: string;
	sharedCount: number;
	sharedLabels: string[];
}

export interface WorkspaceVisualization {
	nodes: WorkspaceKGNode[];
	edges: WorkspaceKGEdge[];
}

export const getWorkspaces = async (options?: ApiRequestOptions): Promise<WorkspaceItem[]> =>
	apiJson<WorkspaceItem[]>("/workspace", { signal: options?.signal });

export const createWorkspace = async (name: string, options?: ApiRequestOptions): Promise<WorkspaceItem> =>
	apiJson<WorkspaceItem>("/workspace", {
		method: "POST",
		body: JSON.stringify({ name }),
		signal: options?.signal,
	});

export const updateWorkspace = async (workspaceId: string, name: string): Promise<WorkspaceItem> =>
	apiJson<WorkspaceItem>(`/workspace/${workspaceId}`, {
		method: "PATCH",
		body: JSON.stringify({ name }),
	});

export const deleteWorkspace = async (workspaceId: string): Promise<{ id: string }> =>
	apiJson<{ id: string }>(`/workspace/${workspaceId}`, { method: "DELETE" });

export const getWorkspaceVisualization = async (
	workspaceId: string,
	options?: ApiRequestOptions,
): Promise<WorkspaceVisualization> =>
	apiJson<WorkspaceVisualization>(`/workspace/${workspaceId}`, { signal: options?.signal });

export const getWorkspaceHierarchy = async (
	workspaceId: string,
	options?: ApiRequestOptions,
): Promise<KnowledgeGraphHierarchyResponse> =>
	apiJson<KnowledgeGraphHierarchyResponse>(`/workspace/${workspaceId}/hierarchy`, { signal: options?.signal });

/**
 * Create a new knowledge graph
 */
export const createKnowledgeGraph = async (
	name: string,
	description?: string | null,
	options?: ApiRequestOptions,
): Promise<KnowledgeGraph> => {
	return apiJson<KnowledgeGraph>("/knowledge-graph", {
		method: "POST",
		body: JSON.stringify({ name, description }),
		signal: options?.signal,
	});
};

/**
 * Update a knowledge graph (e.g. rename)
 */
export const updateKnowledgeGraph = async (
	id: string,
	updates: { name?: string; description?: string | null },
): Promise<KnowledgeGraph> => {
	return apiJson<KnowledgeGraph>(`/knowledge-graph/${id}`, {
		method: "PUT",
		body: JSON.stringify(updates),
	});
};

/**
 * Delete a knowledge graph
 */
export const deleteKnowledgeGraph = async (id: string): Promise<{ id: string }> => {
	return apiJson<{ id: string }>(`/knowledge-graph/${id}`, {
		method: "DELETE",
	});
};

// --- Node CRUD ---

export const listNodes = async (graphId: string, options?: ApiRequestOptions): Promise<GraphNode[]> => {
	return apiJson<GraphNode[]>(`/knowledge-graph/${graphId}/nodes`, {
		signal: options?.signal,
	});
};

export const createNode = async (
	graphId: string,
	body: { id?: string; name: string; elementType: string; typeId?: string | null; metadata?: Record<string, unknown> },
	options?: ApiRequestOptions,
): Promise<GraphNode> => {
	return apiJson<GraphNode>(`/knowledge-graph/${graphId}/nodes`, {
		method: "POST",
		body: JSON.stringify(body),
		signal: options?.signal,
	});
};

export const getNode = async (graphId: string, nodeId: string, options?: ApiRequestOptions): Promise<GraphNode> => {
	return apiJson<GraphNode>(`/knowledge-graph/${graphId}/nodes/${nodeId}`, {
		signal: options?.signal,
	});
};

export const updateNode = async (
	graphId: string,
	nodeId: string,
	body: { name?: string; elementType?: string; typeId?: string | null; metadata?: Record<string, unknown> },
	options?: ApiRequestOptions,
): Promise<GraphNode> => {
	return apiJson<GraphNode>(`/knowledge-graph/${graphId}/nodes/${nodeId}`, {
		method: "PUT",
		body: JSON.stringify(body),
		signal: options?.signal,
	});
};

export const deleteNode = async (
	graphId: string,
	nodeId: string,
	options?: ApiRequestOptions,
): Promise<{ id: string }> => {
	return apiJson<{ id: string }>(`/knowledge-graph/${graphId}/nodes/${nodeId}`, {
		method: "DELETE",
		signal: options?.signal,
	});
};

// --- Edge CRUD ---

export const listEdges = async (graphId: string, options?: ApiRequestOptions): Promise<GraphEdge[]> => {
	return apiJson<GraphEdge[]>(`/knowledge-graph/${graphId}/edges`, {
		signal: options?.signal,
	});
};

export const createEdge = async (
	graphId: string,
	body: {
		id?: string;
		predicateId?: string | null;
		source: string;
		target: string;
		tuplePosition?: number | null;
		label: string;
	},
	options?: ApiRequestOptions,
): Promise<GraphEdge> => {
	return apiJson<GraphEdge>(`/knowledge-graph/${graphId}/edges`, {
		method: "POST",
		body: JSON.stringify(body),
		signal: options?.signal,
	});
};

export const getEdge = async (graphId: string, edgeId: string, options?: ApiRequestOptions): Promise<GraphEdge> => {
	return apiJson<GraphEdge>(`/knowledge-graph/${graphId}/edges/${edgeId}`, {
		signal: options?.signal,
	});
};

export const updateEdge = async (
	graphId: string,
	edgeId: string,
	body: {
		predicateId?: string | null;
		source?: string;
		target?: string;
		tuplePosition?: number | null;
		label?: string;
	},
	options?: ApiRequestOptions,
): Promise<GraphEdge> => {
	return apiJson<GraphEdge>(`/knowledge-graph/${graphId}/edges/${edgeId}`, {
		method: "PUT",
		body: JSON.stringify(body),
		signal: options?.signal,
	});
};

export const deleteEdge = async (
	graphId: string,
	edgeId: string,
	options?: ApiRequestOptions,
): Promise<{ id: string }> => {
	return apiJson<{ id: string }>(`/knowledge-graph/${graphId}/edges/${edgeId}`, {
		method: "DELETE",
		signal: options?.signal,
	});
};
